import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { INR, fmtDate, titleCase, StatusBadge } from "@/lib/format";
import { PageHeader, DataTable, Pager, useList } from "@/components/shared";
import { FormDialog } from "@/components/EntityForm";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileCheck2, Plus, Edit, Search, Eye, FolderCheck, Upload, CheckCircle2, Circle, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

function ChecklistDialog({ app }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/loan/applications/${app.id}/checklist`).then(({ data }) => { setData(data); setConsent(data.consent?.data_sharing || false); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (open) load(); }, [open]);
  const upload = async (name, e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData();
    fd.append("file", f); fd.append("customer_id", app.customer_id); fd.append("loan_app_id", app.id);
    fd.append("category", "Loan"); fd.append("document_name", name);
    setBusy(true);
    try { await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Uploaded"); load(); }
    catch (ex) { toast.error(ex.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); e.target.value = ""; }
  };
  const saveConsent = async (v) => {
    setConsent(v);
    await api.put(`/loan/applications/${app.id}`, { consent_data_sharing: v, consent_purpose: "Loan processing & sharing with the selected lender", consent_date: new Date().toISOString() });
    toast.success(v ? "Consent recorded" : "Consent withdrawn");
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button size="icon" variant="ghost" className="h-8 w-8 text-primary" title="Document checklist" onClick={() => setOpen(true)} data-testid="loan-checklist-btn"><FolderCheck className="h-4 w-4" /></Button>
      <DialogContent className="max-w-lg" data-testid="loan-checklist-dialog">
        <DialogHeader><DialogTitle>Document Checklist — {app.loan_app_id}</DialogTitle></DialogHeader>
        {!data ? <div className="py-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div> : (
          <div className="space-y-2">
            {!app.customer_id && <p className="text-xs text-amber-600">Link this application to a customer to upload documents.</p>}
            {data.checklist.map((c) => (
              <div key={c.name} className="flex items-center gap-2 rounded-lg border border-border p-2.5 text-sm">
                {c.uploaded ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Circle className="h-4 w-4 text-muted-foreground" />}
                <span className="flex-1">{c.name}</span>
                {c.uploaded ? <span className="text-xs text-emerald-600 font-medium">Uploaded</span> : (app.customer_id &&
                  <label><input type="file" className="hidden" onChange={(e) => upload(c.name, e)} accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx" data-testid="checklist-upload-input" />
                    <Button asChild size="sm" variant="outline" disabled={busy}><span className="cursor-pointer"><Upload className="h-3.5 w-3.5 mr-1" />Upload</span></Button></label>)}
              </div>
            ))}
            <label className="flex items-start gap-2 pt-3 text-sm cursor-pointer">
              <Checkbox checked={consent} onCheckedChange={saveConsent} data-testid="loan-consent-checkbox" />
              <span className="text-muted-foreground">Customer consents to collection of these documents and sharing with <b>{app.lender}</b> for loan processing.{data.consent?.date && <span className="block text-xs mt-0.5">Recorded {new Date(data.consent.date).toLocaleDateString("en-IN")}</span>}</span>
            </label>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const STATUS = ["new_lead", "contacted", "interested", "documents_pending", "documents_received", "submitted", "under_review", "additional_info", "eligible", "approved", "rejected", "disbursed", "cancelled", "closed"];

export default function LoanApplications() {
  const { hasPerm } = useAuth();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [lenders, setLenders] = useState([]);
  const params = { ...(q ? { q } : {}), ...(status !== "all" ? { status } : {}) };
  const { items, total, loading, page, setPage, refetch } = useList("/loan/applications", params);
  useEffect(() => {
    api.get("/customers", { params: { limit: 200 } }).then(({ data }) => setCustomers(data.items || [])).catch(() => {});
    api.get("/loan/products").then(({ data }) => setProducts(data)).catch(() => {});
    api.get("/loan/lenders").then(({ data }) => setLenders(data)).catch(() => {});
  }, []);

  const create = async (v) => { await api.post("/loan/applications", v); refetch(); };
  const edit = async (a, v) => { await api.put(`/loan/applications/${a.id}`, v); refetch(); };

  const createFields = [
    { name: "customer_id", label: "Customer", type: "select", required: true, options: customers.map((x) => ({ value: x.id, label: `${x.name} (${x.customer_id})` })) },
    { name: "loan_product", label: "Loan Product", type: "select", required: true, options: products.map((p) => ({ value: p.name, label: p.name })) },
    { name: "lender", label: "Lender", type: "select", required: true, options: lenders.map((l) => ({ value: l.name, label: l.name })) },
    { name: "requested_amount", label: "Requested Amount (₹)", type: "number", required: true },
    { name: "status", label: "Status", type: "select", options: STATUS.map((s) => ({ value: s, label: titleCase(s) })) },
    { name: "notes", label: "Internal Notes", type: "textarea", full: true },
  ];
  const editFields = [
    { name: "status", label: "Status", type: "select", required: true, options: STATUS.map((s) => ({ value: s, label: titleCase(s) })) },
    { name: "lender_reference", label: "Lender Reference No (required for Approved/Disbursed)" },
    { name: "sanctioned_amount", label: "Sanctioned Amount (₹)", type: "number" },
    { name: "disbursement_amount", label: "Disbursed Amount (₹)", type: "number" },
    { name: "disbursement_date", label: "Disbursement Date", type: "date" },
    { name: "rejection_reason", label: "Rejection Reason (required if Rejected)" },
    { name: "cancellation_reason", label: "Cancellation Reason (required if Cancelled)" },
    { name: "doc_verification_status", label: "Doc Verification", type: "select", options: ["pending", "verified", "incomplete"].map((s) => ({ value: s, label: titleCase(s) })) },
  ];
  const columns = [
    { key: "loan_app_id", header: "App ID", render: (r) => <span className="font-mono text-xs">{r.loan_app_id}</span> },
    { key: "customer_name", header: "Customer", render: (r) => <span className="font-semibold">{r.customer_name || "—"}</span> },
    { key: "loan_product", header: "Product", render: (r) => <span className="text-sm">{r.loan_product}</span> },
    { key: "lender", header: "Lender", render: (r) => <span className="text-sm">{r.lender}</span> },
    { key: "requested_amount", header: "Requested", tdClass: "text-right", thClass: "text-right", render: (r) => <span className="tabular">{INR(r.requested_amount)}</span> },
    { key: "disbursement_amount", header: "Disbursed", tdClass: "text-right", thClass: "text-right", render: (r) => <span className="tabular font-semibold text-emerald-600">{r.status === "disbursed" ? INR(r.disbursement_amount) : "—"}</span> },
    { key: "status", header: "Status", render: (r) => <StatusBadge value={r.status} /> },
    { key: "docs", header: "Docs", render: (r) => {
      const p = r.doc_progress || { percent: 0, uploaded: 0, required: 0 };
      const tone = p.percent >= 100 ? "bg-emerald-500" : p.percent >= 50 ? "bg-amber-500" : "bg-rose-500";
      return (
        <div className="w-28" data-testid="loan-doc-progress" title={`${p.uploaded}/${p.required} documents`}>
          <div className="flex items-center justify-between mb-0.5"><span className="text-[10px] font-semibold tabular">{p.percent}%</span><span className="text-[10px] text-muted-foreground tabular">{p.uploaded}/{p.required}</span></div>
          <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden"><div className={`h-full rounded-full ${tone} transition-all`} style={{ width: `${p.percent}%` }} /></div>
        </div>
      );
    } },
    { key: "actions", header: "", render: (r) => (
      <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
        {r.customer_id && <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => nav(`/customers/${r.customer_id}`)} title="View 360"><Eye className="h-4 w-4" /></Button>}
        <ChecklistDialog app={r} />
        {hasPerm("loans:edit") && (
          <FormDialog title={`Update ${r.loan_app_id}`} description="Approved/Disbursed requires a lender reference number (verified source)." fields={editFields} initial={{ status: r.status, lender_reference: r.lender_reference }} onSubmit={(v) => edit(r, v)} submitLabel="Update"
            trigger={<Button size="icon" variant="ghost" className="h-8 w-8" data-testid="loan-app-edit-btn"><Edit className="h-4 w-4" /></Button>} />
        )}
      </div>
    ) },
  ];
  return (
    <div>
      <PageHeader title="Loan Applications" subtitle={`${total} applications · requested ≠ sanctioned ≠ disbursed`} icon={FileCheck2}
        actions={hasPerm("loans:add") && (
          <FormDialog title="New Loan Application" fields={createFields} onSubmit={create} submitLabel="Create Application" testId="create-loan-app-dialog"
            trigger={<Button data-testid="create-loan-app-btn"><Plus className="h-4 w-4 mr-1.5" /> New Application</Button>} />
        )} />
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-9" placeholder="Search app ID, customer, lender ref..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="loan-app-search-input" />
        </div>
        <Select value={status} onValueChange={setStatus}><SelectTrigger className="w-48" data-testid="loan-app-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Statuses</SelectItem>{STATUS.map((s) => <SelectItem key={s} value={s}>{titleCase(s)}</SelectItem>)}</SelectContent></Select>
      </div>
      <DataTable columns={columns} rows={items} loading={loading} rowTestId="loan-app-table-row" />
      <Pager page={page} limit={25} total={total} onPage={setPage} />
    </div>
  );
}
